import type { PublicSiteSettingsSummary } from '@starkey/shared'

const SITE_SETTINGS_CACHE_KEY = 'starkey.site-settings'
const THEME_PRESETS = new Set(['SUNSET', 'OCEAN', 'FOREST', 'AURORA', 'ROSE'])
const LISTING_MODES = new Set(['PRODUCT', 'SPONSOR'])

const fallbackSiteSettings: PublicSiteSettingsSummary = {
  brandName: 'StarKey',
  pageTitle: 'StarKey 发卡站',
  siteDescription: 'StarKey 是一个用于展示商品、记录赞助订单并自动发放内容的在线发卡站。',
  listingMode: 'PRODUCT',
  homeAnnouncement: '欢迎来到 StarKey。\n这里用于展示商品、记录赞助订单，并在支付完成后自动发放内容。',
  homeHeroImageUrl: '',
  homeFullBleed: false,
  footerYear: '2026',
  timeZone: 'Asia/Shanghai',
  themePreset: 'SUNSET',
  beianNo: '',
}

export function getFallbackSiteSettings() {
  return fallbackSiteSettings
}

function normalizeSiteSettings(parsed: Partial<PublicSiteSettingsSummary>) {
  if (
    typeof parsed.brandName !== 'string' ||
    typeof parsed.pageTitle !== 'string' ||
    typeof parsed.siteDescription !== 'string' ||
    typeof parsed.homeAnnouncement !== 'string' ||
    typeof parsed.footerYear !== 'string'
  ) {
    return null
  }

  return {
    brandName: parsed.brandName,
    pageTitle: parsed.pageTitle,
    siteDescription: parsed.siteDescription,
    listingMode: typeof parsed.listingMode === 'string' && LISTING_MODES.has(parsed.listingMode) ? parsed.listingMode : 'PRODUCT',
    homeAnnouncement: parsed.homeAnnouncement,
    homeHeroImageUrl: typeof parsed.homeHeroImageUrl === 'string' ? parsed.homeHeroImageUrl : '',
    homeFullBleed: parsed.homeFullBleed === true,
    footerYear: parsed.footerYear,
    timeZone: typeof parsed.timeZone === 'string' && parsed.timeZone.trim() ? parsed.timeZone : 'Asia/Shanghai',
    themePreset:
      typeof parsed.themePreset === 'string' && THEME_PRESETS.has(parsed.themePreset) ? parsed.themePreset : 'SUNSET',
    beianNo: typeof parsed.beianNo === 'string' ? parsed.beianNo : '',
  } satisfies PublicSiteSettingsSummary
}

export function readBootSiteSettings() {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const parsed = (window as Window & { __STARKEY_SITE_SETTINGS__?: Partial<PublicSiteSettingsSummary> }).__STARKEY_SITE_SETTINGS__
    if (!parsed || typeof parsed !== 'object') {
      return null
    }

    return normalizeSiteSettings(parsed)
  } catch {
    return null
  }
}

export function readCachedSiteSettings() {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const raw = window.localStorage.getItem(SITE_SETTINGS_CACHE_KEY)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as Partial<PublicSiteSettingsSummary>
    if (!parsed || typeof parsed !== 'object') {
      return null
    }

    return normalizeSiteSettings(parsed)
  } catch {
    return null
  }
}

export function writeCachedSiteSettings(settings: PublicSiteSettingsSummary) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(SITE_SETTINGS_CACHE_KEY, JSON.stringify(settings))
  } catch {
    // ignore write failures
  }
}
